import React, { useEffect, useState, useRef, useMemo } from "react";
import ReactPlayer from "react-player";
import UserRepository from '../../../core/repository/UserRepository';
import { Box, IconButton, Tooltip, Slider, Typography, Menu, MenuItem, useMediaQuery, useTheme } from "@mui/material";
import FastRewindIcon from "@mui/icons-material/FastRewind";
import FastForwardIcon from "@mui/icons-material/FastForward";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import ClosedCaptionIcon from "@mui/icons-material/ClosedCaption";
import ClosedCaptionDisabledIcon from "@mui/icons-material/ClosedCaptionDisabled";
import { useSelector } from "react-redux";
import _ from "lodash";
import { useWatermarkProtection } from '../../../hook/useVideoWatermarkProtection';

const VideoPlayerComponent = ({ videoUrl, tumbnailUrl, path, tracks = [] }) => {
  const [userInfo, setUserInfo] = useState({ name: '', contact: '', email: '' });
  const [watermarkKey, setWatermarkKey] = useState(0);
  const [showControls, setShowControls] = useState(false);
  const [isRealFullscreen, setIsFullscreen] = useState(false);
  // CSS-based fullscreen used when the element Fullscreen API is unavailable
  // (e.g. WKWebView). Keeps the watermark overlay visible, unlike the native
  // video fullscreen that webkitEnterFullscreen() would open.
  const [isFakeFullscreen, setIsFakeFullscreen] = useState(false);
  const isFullscreen = isRealFullscreen || isFakeFullscreen;
  const [trackError, setTrackError] = useState(false);
  
  const [anchorEl, setAnchorEl] = useState(null);
  const [speedAnchorEl, setSpeedAnchorEl] = useState(null);
  const [selectedTrack, setSelectedTrack] = useState(0);

  const [playing, setPlaying] = useState(false);
  const [playedSeconds, setPlayedSeconds] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.8);
  const [muted, setMuted] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [captionsDisabledByUser, setCaptionsDisabledByUser] = useState(false);

  const hasTriggeredRef = useRef(false);
  const playerRef = useRef(null);
  const containerRef = useRef(null);
  const hideControlsTimeout = useRef(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const verifyData = useSelector((state) => state.auth.get("verifyData"));
  const [cloudfronturl, setCloduFrontUrl] = useState("");

  const sendTamperingAlert = async () => {
    try {
      await UserRepository.temperingAlertMail();
    } catch (e) {
      console.error("Tampering alert error:", e);
    }
  };

  useWatermarkProtection({
    wrapperSelector: '.watermark-protection',
    watermarkSelector: '.w-watermark',
    onTamper: () => {
      if (!hasTriggeredRef.current) {
        hasTriggeredRef.current = true;
        sendTamperingAlert();
      }
      requestAnimationFrame(() => {
        setWatermarkKey(prev => prev + 1);
      });
    }
  });

  const enforceNativePlaybackRate = (rate) => {
    try {
      const internalPlayer = playerRef.current?.getInternalPlayer();
      if (internalPlayer) {
        internalPlayer.defaultPlaybackRate = rate;
        internalPlayer.playbackRate = rate;
      }
    } catch (err) {
      console.error("Failed to enforce playback rate:", err);
    }
  };

  useEffect(() => {
    enforceNativePlaybackRate(playbackRate);
  }, [playbackRate, playing]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      const internalPlayer = playerRef.current?.getInternalPlayer();
      const fullscreenElement =
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement ||
        (internalPlayer && internalPlayer.webkitDisplayingFullscreen);

      setIsFullscreen(!!fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("mozfullscreenchange", handleFullscreenChange);
    document.addEventListener("msfullscreenchange", handleFullscreenChange);

    const internalPlayer = playerRef.current?.getInternalPlayer();
    if (internalPlayer) {
      internalPlayer.addEventListener("webkitbeginfullscreen", handleFullscreenChange);
      internalPlayer.addEventListener("webkitendfullscreen", handleFullscreenChange);
    }

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
      document.removeEventListener("mozfullscreenchange", handleFullscreenChange);
      document.removeEventListener("msfullscreenchange", handleFullscreenChange);
      if (internalPlayer) {
        internalPlayer.removeEventListener("webkitbeginfullscreen", handleFullscreenChange);
        internalPlayer.removeEventListener("webkitendfullscreen", handleFullscreenChange);
      }
    };
  }, []);

  useEffect(() => {
    const getUser = async () => {
      try {
        const user = await UserRepository.getUserDetail();
        setUserInfo({
          name: `${user?.data?.first_name || ""} ${user?.data?.last_name || ""}`.trim(),
          contact: user?.data?.phone_number || '',
          email: user?.data?.email || '',
        });
      } catch (err) {
        console.error("Failed fetching user context", err);
      }
    };
    getUser();
  }, []);

  useEffect(() => {
    setCloduFrontUrl(_.get(verifyData, "video_url"));
  }, [verifyData]);

  const { formattedTracks, defaultTrackIndex } = useMemo(() => {
    if (trackError) return { formattedTracks: [], defaultTrackIndex: -1 };

    const rawList = (tracks || []).filter(Boolean);
    const valid = [];
    const seenSrcs = new Set();

    rawList.forEach((track) => {
      const rawSrc = track.src || track.url || track.path || track.file_url || track.file_name || "";
      if (!rawSrc) return;
      const isAbsolute = rawSrc.startsWith("http://") || rawSrc.startsWith("https://") || rawSrc.startsWith("blob:");
      const fullSrc = !isAbsolute && cloudfronturl ? `${cloudfronturl}${rawSrc}` : rawSrc;

      if (seenSrcs.has(fullSrc)) return;
      seenSrcs.add(fullSrc);

      valid.push({
        kind: track.kind || "subtitles",
        src: fullSrc,
        srcLang: track.srclang || track.srcLang || "en",
        label: track.label || "English",
        isDefault: track.default,
      });
    });

    if (valid.length === 0) {
      return { formattedTracks: [], defaultTrackIndex: -1 };
    }

    const explicitIndex = valid.findIndex((t) => t.isDefault);
    const chosenDefaultIndex = explicitIndex !== -1 ? explicitIndex : 0;

    const formatted = valid.map((t, index) => ({
      kind: t.kind,
      src: t.src,
      srcLang: t.srcLang,
      label: t.label,
      default: index === chosenDefaultIndex,
    }));

    return { formattedTracks: formatted, defaultTrackIndex: chosenDefaultIndex };
  }, [tracks, cloudfronturl, trackError]);

  const finalVideoUrl = videoUrl || (cloudfronturl && path ? `${cloudfronturl}${path}` : path || "");

  // Enable exactly one default subtitle track programmatically on initial load only
  const handleReady = () => {
    enforceNativePlaybackRate(playbackRate);
    try {
      const internalPlayer = playerRef.current?.getInternalPlayer();
      if (internalPlayer && internalPlayer.textTracks && internalPlayer.textTracks.length > 0) {
        for (let i = 0; i < internalPlayer.textTracks.length; i++) {
          const shouldShow = !captionsDisabledByUser && (i === (selectedTrack !== -1 ? selectedTrack : defaultTrackIndex));
          internalPlayer.textTracks[i].mode = shouldShow ? "showing" : "disabled";
        }
      }
    } catch (e) {
      console.error("Safari / Firefox Track Mode Error:", e);
    }
  };

  const handleTrackChange = (index) => {
    setSelectedTrack(index);
    setAnchorEl(null);

    if (index === -1) {
      setCaptionsDisabledByUser(true);
    } else {
      setCaptionsDisabledByUser(false);
    }

    const internalPlayer = playerRef.current?.getInternalPlayer();
    if (internalPlayer && internalPlayer.textTracks) {
      for (let i = 0; i < internalPlayer.textTracks.length; i++) {
        internalPlayer.textTracks[i].mode = (i === index) ? "showing" : "disabled";
      }
    }
  };

  const handleSpeedChange = (rate) => {
    setPlaybackRate(rate);
    enforceNativePlaybackRate(rate);
    setSpeedAnchorEl(null);
  };

  const handleSeekChange = (_, newValue) => {
    setPlayedSeconds(newValue);
    playerRef.current?.seekTo(newValue, "seconds");
    try {
      const internalPlayer = playerRef.current?.getInternalPlayer();
      if (internalPlayer && internalPlayer.textTracks) {
        for (let i = 0; i < internalPlayer.textTracks.length; i++) {
          const shouldShow = !captionsDisabledByUser && (i === selectedTrack);
          internalPlayer.textTracks[i].mode = shouldShow ? "showing" : "disabled";
        }
      }
    } catch (e) {
      console.error("Seek track re-apply error:", e);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  const handleFullscreenToggle = () => {
    const container = containerRef.current;
    if (!container) return;

    if (isFakeFullscreen) {
      setIsFakeFullscreen(false);
      return;
    }

    if (!isRealFullscreen) {
      if (container.requestFullscreen) {
        container.requestFullscreen();
      } else if (container.webkitRequestFullscreen) {
        container.webkitRequestFullscreen();
      } else if (container.mozRequestFullScreen) {
        container.mozRequestFullScreen();
      } else if (container.msRequestFullscreen) {
        container.msRequestFullscreen();
      } else {
        setIsFakeFullscreen(true);
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    }
  };

  useEffect(() => {
    if (!isFakeFullscreen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setIsFakeFullscreen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isFakeFullscreen]);

  const handleMouseMove = () => {
    setShowControls(true);
    if (hideControlsTimeout.current) {
      clearTimeout(hideControlsTimeout.current);
    }
    hideControlsTimeout.current = setTimeout(() => setShowControls(false), 3000);
  };

  return (
    <Box
      className="watermark-protection"
      ref={containerRef}
      sx={{
        position: "relative",
        width: "100%",
        maxWidth: isFullscreen ? "100%" : "calc(100vw - 150px)",
        height: isFullscreen ? "100vh" : "auto",
        aspectRatio: isFullscreen ? "unset" : "16/9",
        backgroundColor: "black",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginBottom: isFullscreen ? 0 : 2,
        overflow: "hidden",
        isolation: "isolate",
        ...(isFakeFullscreen && {
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          zIndex: 2147483647,
        }),
      }}
      onMouseMove={handleMouseMove}
      onMouseLeave={() => setShowControls(false)}
      onContextMenu={(e) => e.preventDefault()}
    >
      <ReactPlayer
        ref={playerRef}
        key={`${finalVideoUrl}-${formattedTracks.length}`}
        url={finalVideoUrl}
        controls={false}
        playing={playing}
        volume={volume}
        muted={muted}
        playbackRate={playbackRate}
        width="100%"
        height="100%"
        light={tumbnailUrl ? tumbnailUrl : false}
        onReady={handleReady}
        onPlay={() => enforceNativePlaybackRate(playbackRate)} // Prevents Safari from resetting speed to 1x when play is pressed
        onProgress={(state) => setPlayedSeconds(state.playedSeconds)}
        onDuration={(d) => setDuration(d)}
        onError={() => setTrackError(true)}
        config={{
          file: {
            attributes: {
              crossOrigin: "anonymous",
              playsInline: true,
              disablePictureInPicture: true,
              'webkit-playsinline': 'true',
            },
            tracks: formattedTracks,
          },
        }}
      />

      {/* Watermark Overlay */}
      <Box
        key={watermarkKey}
        className="w-watermark"
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: 'none',
          zIndex: 99,
          overflow: 'hidden',
        }}
      >
        {[...Array(isMobile ? 4 : 9)].map((_, index) => (
          <Box
            key={index}
            sx={{
              position: 'absolute',
              left: `${(index % (isMobile ? 2 : 3)) * (isMobile ? 52 : 38) + (isMobile ? 4 : 2)}%`,
              top: `${Math.floor(index / (isMobile ? 2 : 3)) * (isMobile ? 32 : 30) + (isMobile ? 5 : 2)}%`,
              transform: 'rotate(-20deg)',
              opacity: 0.25,
              fontSize: isMobile ? '7px' : '12px',
              color: '#171515ff',
              whiteSpace: 'nowrap',
              userSelect: 'none',
              maxWidth: isMobile ? '45%' : '30%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{userInfo.name}</div>
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{userInfo.contact}</div>
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{userInfo.email}</div>
          </Box>
        ))}
      </Box>

      {/* Custom Controls */}
      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          padding: "10px 16px",
          background: "linear-gradient(transparent, rgba(0,0,0,0.85))",
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
          zIndex: 100,
          opacity: showControls ? 1 : 0,
          transition: "opacity 0.3s ease-in-out",
          pointerEvents: showControls ? "auto" : "none",
        }}
      >
        <Slider
          size="small"
          min={0}
          max={duration || 100}
          value={playedSeconds}
          onChange={handleSeekChange}
          sx={{ color: "#1976d2", padding: "4px 0" }}
        />

        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton onClick={() => setPlaying(!playing)} size="small" sx={{ color: "white" }}>
              {playing ? <PauseIcon /> : <PlayArrowIcon />}
            </IconButton>

            <IconButton onClick={() => handleSeekChange(null, Math.max(0, playedSeconds - 10))} size="small" sx={{ color: "white" }}>
              <FastRewindIcon />
            </IconButton>

            <IconButton onClick={() => handleSeekChange(null, Math.min(duration, playedSeconds + 10))} size="small" sx={{ color: "white" }}>
              <FastForwardIcon />
            </IconButton>

            <IconButton onClick={() => setMuted(!muted)} size="small" sx={{ color: "white" }}>
              {muted ? <VolumeOffIcon /> : <VolumeUpIcon />}
            </IconButton>

            <Typography variant="caption" sx={{ color: "white", ml: 1 }}>
              {formatTime(playedSeconds)} / {formatTime(duration)}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {/* Playback Speed Selector */}
            <Tooltip title="Playback Speed">
              <IconButton onClick={(e) => setSpeedAnchorEl(e.currentTarget)} size="small" sx={{ color: "white" }}>
                <Typography variant="caption" sx={{ fontWeight: "bold" }}>{playbackRate}x</Typography>
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={speedAnchorEl}
              open={Boolean(speedAnchorEl)}
              onClose={() => setSpeedAnchorEl(null)}
              container={containerRef.current}
              disablePortal
              slotProps={{
                paper: {
                  sx: {
                    zIndex: 9999,
                    backgroundColor: "rgba(28, 28, 28, 0.95)",
                    color: "black",
                  },
                },
              }}
            >
              {[0.5, 0.75, 1, 1.25, 1.5, 1.75, 2].map((rate) => (
                <MenuItem
                  key={rate}
                  selected={playbackRate === rate}
                  onClick={() => handleSpeedChange(rate)}
                >
                  {rate}x
                </MenuItem>
              ))}
            </Menu>

            {/* Caption Selector for Custom Controls */}
            {formattedTracks.length > 0 && (
              <>
                <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} size="small" sx={{ color: "white" }}>
                  {!captionsDisabledByUser && selectedTrack !== -1 ? <ClosedCaptionIcon /> : <ClosedCaptionDisabledIcon />}
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={() => setAnchorEl(null)}
                  container={containerRef.current}
                  disablePortal
                  slotProps={{
                    paper: {
                      sx: {
                        zIndex: 9999,
                        backgroundColor: "rgba(28, 28, 28, 0.95)",
                        color: "black",
                      },
                    },
                  }}
                >
                  <MenuItem onClick={() => handleTrackChange(-1)}>Off</MenuItem>
                  {formattedTracks.map((track, i) => (
                    <MenuItem key={i} selected={!captionsDisabledByUser && selectedTrack === i} onClick={() => handleTrackChange(i)}>
                      {track.label}
                    </MenuItem>
                  ))}
                </Menu>
              </>
            )}

            <IconButton onClick={handleFullscreenToggle} size="small" sx={{ color: "white" }}>
              {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default VideoPlayerComponent;
